'use client';

/**
 * AuthProvider — side-effect-only component (returns `null`) that keeps the
 * React Query cache in sync with Supabase auth state for the CSR app.
 *
 * It is the single writer of auth-driven cache invalidation:
 *   - On any auth event, invalidate `queryKeys.auth.*` so `useCurrentUser` /
 *     `useProfile` re-read the new session.
 *   - On SIGNED_OUT, `queryClient.clear()` drops ALL cached data so the next
 *     user who signs in on the same browser never sees the previous user's
 *     workspaces / tasks / wallet (security). Route guards then redirect the
 *     now-unauthenticated client to /login.
 *
 * Mounted once in the root layout inside QueryProvider, as a sibling of the
 * page tree (it provides no context — auth state lives in React Query and is
 * read via the `use-current-user` / `use-profile` hooks).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { queryKeys } from '@/lib/query/query-keys';
import { purgeLocalUserData } from '@/lib/db/offline-db';

export function AuthProvider() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Wipe every cached query so no data crosses a user boundary.
        queryClient.clear();
        // UI-05: clearing React Query alone left the previous user's data on
        // disk — the Dexie database and the service worker's cached
        // authenticated /api/ responses both survived a sign-out, so on a
        // shared machine their balances, notifications and task payloads
        // stayed readable (and servable offline).
        void purgeLocalUserData();
        return;
      }
      // SIGNED_IN / TOKEN_REFRESHED / USER_UPDATED / INITIAL_SESSION:
      // re-read identity + profile (role / onboarding / avatar may differ).
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  return null;
}
